<template>
  <div :class="wrapperClassName" v-if="visibleIfResult">
    <template v-if="!hidden">
      <label :for="idInput" :class="labelClassName" v-dompurify-html="labelText"></label>

      <span class="help-trigger" title="Hilfe anzeigen" @click="toggleHelpText" v-if="help">
        <font-awesome-icon icon="question-circle" size="lg" />
      </span>

      <Message
        v-show="shouldShowHelp"
        closable
        severity="info"
        role="alert"
        class="mb-2 form-info-message"
        @close="toggleHelpText"
      >
        <span v-dompurify-html="help"></span>
      </Message>
    </template>

    <Select2
      :id="idInput"
      :data-vv-as="labelText"
      :data-wdw-code="code"
      :name="name"
      :settings="select2Settings"
      :options="filteredSelect2Options"
      :aria-invalid="hasError || hasServerError"
      ref="select2"
      class=""
      v-model="value"
      @select="select2SelectEvent($event)"
      :hidden="hidden"
      :disabled="disabled"
    />

    <template v-if="!hidden">
      <span
        v-show="hasError"
        :id="idErrorText"
        aria-atomic="true"
        aria-live="polite"
        class="text-danger form-error-message"
      >
        {{ error }}
      </span>
      <span
        v-show="hasServerError"
        :id="idErrorText"
        aria-atomic="true"
        aria-live="polite"
        class="text-danger form-error-message"
      >
        {{ serverError }}
      </span>
    </template>
  </div>
</template>

<script>
import { computed, watch, onMounted, toRefs, readonly } from 'vue';
import { useField } from 'vee-validate';
import Select2 from 'vue3-select2-component';
import useToggler from '@/composables/general/useToggler';
import Message from 'primevue/message';
import { ref } from 'vue';

// TODO -> refactor, test  & style
export default {
  name: 'form-control-select',
  emits: ['validate', 'selected'],
  props: {
    name: {
      required: true,
      type: String,
    },
    code: {
      required: false,
      type: String,
      default: '',
    },
    label: {
      required: false,
      type: String,
      default: '',
    },
    options: {
      required: true,
      type: Array,
      default() {
        return [];
      },
    },
    required: {
      type: Boolean,
      default: false,
    },
    multiselect: {
      required: false,
      type: Boolean,
      default: false,
    },
    defaultvalue: {
      required: false,
      type: Array,
      default: () => [],
    },
    cachevalue: {
      required: false,
      type: Array,
      default: () => [],
    },
    additionalLabelClasses: {
      required: false,
      type: String,
      default: '',
    },
    shouldcache: {
      required: false,
      type: Boolean,
      default: false,
    },
    prefixId: {
      required: false,
      type: String,
      default: 'wdw-bms-attr',
    },
    error: {
      required: false,
      type: String,
      default: '',
    },
    serverError: {
      required: false,
      type: String,
      default: '',
    },
    help: {
      required: false,
      type: String,
      default: '',
    },
    hidden: {
      required: false,
      type: Boolean,
      default: false,
    },
    visibleIfResult: {
      required: false,
      type: Boolean,
      default: true,
    },
    allowClear: {
      required: false,
      type: Boolean,
      default: false,
    },
    isPublic: {
      type: Boolean,
      default: true,
    },
    disabled: {
      type: Boolean,
      default: false,
    },
    reload: {
      type: Number,
      required: false,
    },
    overridevalue: {
      // Use this to override value (e.g. when parent wants to change the value of this component)
      // Only triggered if the overridden value is different than actual value
      required: false,
      type: Array,
      default: () => [],
    },
    dependantvalues: {
      // T22832: Contains the key-value pairings of other <select> result
      // This determines which options that can be shown in the selection based on other <select>'s values
      // Example: { 'code1': ['value1', 'value2'], 'code2': [ 'value3' ], .... }
      required: false,
      type: Object,
      default: function () {
        return {};
      },
    },
  },

  setup(props, context) {
    const {
      name,
      required,
      code,
      defaultvalue,
      cachevalue,
      shouldcache,
      label,
      options,
      prefixId,
      multiselect,
      error,
      serverError,
      allowClear,
      additionalLabelClasses,
      isPublic,
      reload,
    } = toRefs(props);

    // Contains JS DOM object for select2 container label
    let select2Elem = null;
    let select2ElemBox = null;

    const { toggleValue: shouldShowHelp, toggle: toggleHelpText } = useToggler();

    const filteredSelect2Options = ref([]);
    const lastCheckedDependantKeyValue = ref(null);

    //+---------------------+
    //|    early computed() |
    //+---------------------+
    const idInput = computed(() => {
      return prefixId.value + '-select-' + name.value;
    });

    //+---------------------+
    //|    Other functions  |
    //+---------------------+
    function initSelect2() {
      const selectElem = document.getElementById(idInput.value);
      if (selectElem) {
        for (const sibling of selectElem.parentNode.children) {
          if (sibling.classList.contains('select2-container--default')) {
            select2Elem = sibling;
            select2ElemBox = sibling.childNodes[0].childNodes[0];
          }
        }
      }
    }
    function updateSelect2AriaLabel() {
      if (label && label.value !== '' && select2Elem !== null) {
        const s2e = select2Elem;
        const sel2SingleSearch = s2e.querySelectorAll('.select2-selection--single')[0];
        const sel2Rendered = s2e.querySelectorAll('.select2-selection__rendered')[0];
        // For single search
        if (sel2SingleSearch) {
          sel2SingleSearch.removeAttribute('aria-describedby aria-labelledby');
          sel2SingleSearch.setAttribute('aria-label', label.value + ' ' + sel2Rendered.textContent);
        }

        // For multiselect with text input field
        const sel2MultiselectedValues = s2e.querySelectorAll('.select2-selection__choice');
        const sel2MultiselectInput = s2e.querySelectorAll('.select2-search__field');
        if (sel2MultiselectInput.length > 0 && sel2MultiselectedValues) {
          let pickedValues = '';
          sel2MultiselectedValues.forEach(sel => {
            pickedValues += sel.getAttribute('title') + ', ';
          });
          sel2MultiselectInput[0].setAttribute('aria-label', label.value + ': ' + pickedValues);
        }
      }
    }
    function getOptionByValue(optValue) {
      if (optValue == '') return null;
      const selected = options.value.filter(opt => opt.id === optValue);
      return selected.size == 0 ? null : selected[0];
    }
    function validate(value) {
      let finalvalue = [];
      if (value !== null) {
        finalvalue = Array.isArray(value) ? value : Array.from([value]);
      }
      const returnValue = {
        value: finalvalue,
        valueHuman: [],
        validated: true,
        name: name.value,
        code: code.value,
        type: 'valuelist',
      };
      finalvalue.forEach(v => {
        const optVal = getOptionByValue(v);
        if (optVal !== null) {
          const optValHuman = readonly(optVal);
          if (optValHuman) {
            returnValue.valueHuman.push(optValHuman.text);
          }
        }
      });

      if (required.value) {
        returnValue.validated = finalvalue.length > 0 && finalvalue[0] !== '';
      } else {
        returnValue.validated = true;
      }

      if (shouldcache.value) {
        localStorage.setItem(name.value, returnValue.value);
      }

      // Update labelling for accessibility
      updateSelect2AriaLabel();

      // Update display for select2ElemBox
      if (select2ElemBox) {
        select2ElemBox.style['border-color'] = returnValue.validated ? '#000' : '#f00';
      }

      // Emit event
      context.emit('validate', returnValue);
      return returnValue.validated ? true : error.value;
    }
    const initialValue =
      shouldcache.value && cachevalue.value && cachevalue.value.length > 0 ? cachevalue.value : defaultvalue.value;
    const { errorMessage, value } = useField(name, validate, {
      initialValue: initialValue,
    });

    function select2SelectEvent({ id, text }) {
      // do not emit direct select2 event. Event does not contain empty value when selection was cleared
      // instead find select element and emit current value
      if (select2Elem) {
        const selectElem = document.getElementById(idInput.value);
        if (selectElem) {
          context.emit('selected', { field: name.value, value: selectElem.value });
        }
      }
    }

    //+---------------------+
    //|    computed()       |
    //+---------------------+
    const hasError = computed(() => {
      return errorMessage.value != undefined;
    });
    const hasServerError = computed(() => {
      return serverError.value != undefined && serverError.value !== '';
    });
    const wrapperClassName = computed(() => {
      return {
        'form-group': true,
        'has-danger': hasError.value || hasServerError.value,
      };
    });
    const labelClassName = computed(() => {
      return additionalLabelClasses.value + ' ' + (hasError.value || hasServerError.value ? 'text-danger' : '');
    });
    const labelText = computed(() => {
      return (
        label.value +
        (required.value ? '*' : '') +
        (isPublic.value ? '' : ' - <span class="text-muted">Diese Daten werden nicht veröffentlicht.</span>')
      );
    });
    const select2Options = computed(() => {
      // We need to check whether select2Elem is initialized first
      // For some cases, computed() can be called even before select2 component is mounted
      if (select2Elem === null) return [...options.value];
      const s2Options = [...options.value];
      if (defaultvalue.value.length == 0 && !multiselect.value) {
        s2Options.unshift({ id: '', text: 'Bitte wählen Sie' });
      }

      return s2Options;
    });
    const select2Settings = computed(() => {
      return {
        multiple: multiselect.value,
        selectionCssClass: 'wdw-select2', // this does not work in v-select2-component, but is the correct approach according to docs
        containerCssClass: 'wdw-select2', // this works but is a bug as it's supposed to set the class on the container
        width: '100%',
        placeholder: label.value,
        minimumResultsForSearch: multiselect.value ? 20 : -1,
        allowClear: allowClear.value,
      };
    });
    const idErrorText = computed(() => {
      return prefixId.value + '-select-' + name.value + '-error';
    });

    function arrayEquals(a, b) {
      if (!a || !b) {
        return false;
      }
      if (!Array.isArray(a)) {
        a = [a];
      }
      if (!Array.isArray(b)) {
        b = [b];
      }
      const sortedCopyA = JSON.parse(JSON.stringify(a)).sort();
      const sortedCopyB = JSON.parse(JSON.stringify(b)).sort();
      return (
        Array.isArray(sortedCopyA) &&
        Array.isArray(sortedCopyB) &&
        sortedCopyA.length === sortedCopyB.length &&
        sortedCopyA.every((val, index) => val === sortedCopyB[index])
      );
    }
    //+---------------------+
    //|  Lifecycle Hooks    |
    //+---------------------+
    onMounted(() => {
      initSelect2();
      filteredSelect2Options.value = JSON.parse(JSON.stringify(select2Options.value));
    });
    watch(
      () => defaultvalue.value,
      (nv, ov) => {
        if (!arrayEquals(nv, ov)) {
          value.value = defaultvalue.value;
        }
      }
    );
    watch(
      () => reload.value,
      (nv, ov) => {
        value.value = defaultvalue.value;
      }
    );
    watch(
      () => props.overridevalue,
      (nv, ov) => {
        if (!arrayEquals(nv, ov) && !arrayEquals(nv, value.value)) {
          value.value = nv;
        }
      }
    );

    // T22832
    watch(
      () => props.dependantvalues,
      (nv, ov) => {
        const originalOptions = select2Options.value;

        // If there are no options with attribute dependency we can safely ignore
        if (originalOptions.findIndex(x => x.filter_attr_code != null && x.filter_attr_code != '') == -1) {
          filteredSelect2Options.value = originalOptions;
          return;
        }

        // Check if any dependant values are changed.
        // This is required for changes on dependant values that have nothing to do with this options, but are included anyway
        // E.g in a form with field A, B, C with A dependant on B, value of field C is changed
        // If they are not changed, we don't have to refilter the options
        let dependantValuesChanged = false;
        if (lastCheckedDependantKeyValue.value != null) {
          /**
           *  nv:
           *    { 'sel1': ["a", "b"], 'sel2': ["c"], 'sel3': ["d", "e"] }
           *  lastCheckedDependantKeyValue:
           *    { 'sel1': ["a"] }
           *  intersect and compare
           *    { 'sel1': ["a", "b"] } and { 'sel1': ["a"] }
           *
           *  if the value is different (or if the select field is first initiated), the dependant value was changed and we need to evaluate the options
           */

          const intersectionKeys = Object.keys(lastCheckedDependantKeyValue.value).filter(k =>
            Object.keys(nv).includes(k)
          );
          if (intersectionKeys.length > 0) {
            dependantValuesChanged =
              intersectionKeys.map(k => arrayEquals(lastCheckedDependantKeyValue.value[k], nv[k])).find(x => !x) !=
              undefined;
          }
        } else {
          dependantValuesChanged = true;
        }

        if (!dependantValuesChanged) {
          return;
        }

        let filteredOpts = [];
        // Loop through the list of dependant values
        Object.keys(nv).forEach(selectItemKey => {
          // First, ignore the one with the same code (because you cannot create dependency to your own option)
          // Second, check that there is at least one option that has dependency towards attribute with key "selectItemKey"
          if (selectItemKey != code.value && originalOptions.findIndex(x => x.filter_attr_code == selectItemKey) >= 0) {
            // Array of value from the given dependant
            const filterValue = nv[selectItemKey];
            // If the value is empty, don't filter anything. Use original options
            if (filterValue == null || filterValue == undefined || filterValue.length == 0 || filterValue[0] == '') {
              filteredOpts = originalOptions;
            } else {
              // Filter only options whose filter_attr_code matches selectItemKey and the filter_value is included
              filteredOpts = originalOptions.filter(
                org => org.filter_attr_code == selectItemKey && filterValue.includes(org.filter_value)
              );
            }

            // Store the last checked dependant key-value.
            if (lastCheckedDependantKeyValue.value == null) {
              lastCheckedDependantKeyValue.value = {};
            }
            lastCheckedDependantKeyValue.value[selectItemKey] = filterValue;
          }
        });

        // Empty the current select value if the num of options changes
        // This prevents field having values but not being shown because the possible options have changed
        if (filteredOpts.length != originalOptions.length) {
          value.value = [];
        }

        filteredSelect2Options.value = filteredOpts;
      },
      // deep:true is needed because it watches a JS object
      { deep: true }
    );

    return {
      // Computed
      hasError,
      hasServerError,
      wrapperClassName,
      labelClassName,
      select2Options,
      select2Settings,
      idInput,
      idErrorText,
      shouldShowHelp,
      labelText,
      // Methods
      toggleHelpText,
      select2SelectEvent,
      // VARS
      errorMessage,
      value,
      filteredSelect2Options,
    };
  },

  mixins: [],

  components: {
    Select2,
    Message,
  },
};
</script>

<style lang="scss" scoped>
label {
  display: inline;
  font-weight: bold;
  max-width: 100%;
}
.form-group {
  margin-bottom: 15px;
}
.help-trigger {
  cursor: pointer;
  margin-left: 4px;
}
</style>
